/**
 * Centralized logging utility
 * Logs are suppressed in production environment
 */

const isDev = import.meta.env.DEV;

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

interface LoggerOptions {
  prefix?: string;
  enabled?: boolean;
}

class Logger {
  private prefix: string;
  private enabled: boolean;

  constructor(options: LoggerOptions = {}) {
    this.prefix = options.prefix || '';
    this.enabled = options.enabled ?? isDev;
  }

  private formatMessage(level: LogLevel, args: unknown[]): unknown[] {
    const timestamp = new Date().toISOString();
    const prefix = this.prefix ? `[${this.prefix}]` : '';
    return [`${timestamp} ${prefix}[${level.toUpperCase()}]`, ...args];
  }

  log(...args: unknown[]): void {
    if (this.enabled) {
      console.log(...this.formatMessage('log', args));
    }
  }

  info(...args: unknown[]): void {
    if (this.enabled) {
      console.info(...this.formatMessage('info', args));
    }
  }

  warn(...args: unknown[]): void {
    if (this.enabled) {
      console.warn(...this.formatMessage('warn', args));
    }
  }

  error(...args: unknown[]): void {
    // Always log errors, even in production
    console.error(...this.formatMessage('error', args));
  }

  debug(...args: unknown[]): void {
    if (this.enabled && isDev) {
      console.debug(...this.formatMessage('debug', args));
    }
  }

  /**
   * Create a child logger with a specific prefix
   */
  child(prefix: string): Logger {
    const newPrefix = this.prefix ? `${this.prefix}:${prefix}` : prefix;
    return new Logger({ prefix: newPrefix, enabled: this.enabled });
  }
}

// Default logger instance
export const logger = new Logger();

// Pre-configured loggers for different modules
export const authLogger = logger.child('Auth');
export const apiLogger = logger.child('API');
export const routerLogger = logger.child('Router');

// Factory function for creating custom loggers
export const createLogger = (prefix: string, options?: Omit<LoggerOptions, 'prefix'>): Logger => {
  return new Logger({ prefix, ...options });
};

export default logger;
